import {
    calculateChartRange,
    type MetricChartSpec,
    type MetricSeriesSpec,
} from '@/components/charts/generic';
import type { RespirationDataPoint, ThresholdRange } from '@/domain/behavior';
import { getSwimSpeedUnitLabelByLocale, type Locale } from '@/utils/localeFormatters';

import {
    BEHAVIOR_GREEN,
    BEHAVIOR_LINE_MARKER,
    BEHAVIOR_LINE_TENSION,
    BEHAVIOR_ORANGE,
    BEHAVIOR_RIVERSIDE,
    behaviorBand,
    behaviorPoints,
    behaviorXAxis,
    measurementsFooter,
    swimSpeedGradient,
} from '../behaviorChartSpec';

/** The swim speed band the respiration page colours its context line by. */
const SWIM_SPEED_THRESHOLD: ThresholdRange = { min: 0.5, max: 1.5, optimal: 1 };
const SWIM_SPEED_MIN_MAX = 0.8;
/** Chart.js' default width, which the respiration lines have always been drawn at. */
const RESPIRATION_LINE_WIDTH = 3;

type Certainty = 'certainRespiration' | 'certainActivePercent';

interface RespirationSpecBase {
    points: RespirationDataPoint[];
    t: (key: string) => string;
    locale: Locale;
}

/** The breathing rate in Hz, dashed where the measurement is uncertain. */
function respirationRateSeries(points: RespirationDataPoint[], label: string): MetricSeriesSpec {
    return {
        key: 'respiration',
        label,
        color: BEHAVIOR_RIVERSIDE,
        tension: BEHAVIOR_LINE_TENSION,
        lineWidth: RESPIRATION_LINE_WIDTH,
        uncertain: { points: points.map((point) => !point.certainRespiration) },
        data: behaviorPoints(points, (point) => point.respiration),
    };
}

/** The measurement count, named uncertain where `certainty` says so. */
function respirationFooter(base: RespirationSpecBase, certainty: Certainty) {
    const { points, t, locale } = base;
    return measurementsFooter(
        points,
        (point) => point.numberOfMeasurements,
        (point) => t(point[certainty] ? 'common.measurements' : 'common.uncertainMeasurements'),
        locale
    );
}

/**
 * The respiration page: the breathing rate on the left, swim speed coloured by its threshold
 * band on the right, and the day's measurement count in the tooltip footer.
 */
export function buildRespirationSpec(input: RespirationSpecBase): MetricChartSpec<'line'> {
    const { points, t, locale } = input;
    const rateLabel = t('common.respirationRate');
    const speedLabel = t('common.swimSpeed');
    return {
        kind: 'line',
        ...behaviorXAxis(points, locale),
        series: [
            respirationRateSeries(points, rateLabel),
            {
                key: 'swimSpeed',
                label: speedLabel,
                color: BEHAVIOR_GREEN,
                axis: 'right',
                lineGradient: swimSpeedGradient(SWIM_SPEED_THRESHOLD),
                tension: BEHAVIOR_LINE_TENSION,
                marker: BEHAVIOR_LINE_MARKER,
                data: behaviorPoints(points, (point) => point.swimSpeed),
            },
        ],
        left: {
            label: rateLabel,
            unit: 'Hz',
            ...calculateChartRange(points.map((point) => point.respiration)),
        },
        right: {
            label: speedLabel,
            unit: getSwimSpeedUnitLabelByLocale(locale),
            ...calculateChartRange(
                points.map((point) => point.swimSpeed),
                15,
                true,
                SWIM_SPEED_MIN_MAX
            ),
            alignTicksToLeft: true,
        },
        locale,
        tooltipFooterLines: respirationFooter(input, 'certainRespiration'),
    };
}

/**
 * The dashboard's respiration chart: the breathing rate in Hz, or the active and passive
 * shares of fish in percent, with oxygen on the right when there is any.
 */
export function buildRespirationOverviewSpec(
    input: RespirationSpecBase & { showBreathingRate: boolean }
): MetricChartSpec<'line'> {
    const { points, t, locale, showBreathingRate } = input;
    const oxygenValues = points.map((point) => point.oxygen).filter((value) => value != null);
    const oxygenLabel = t('overview.oxygenLevel');

    const shares = (key: 'activePercent' | 'passivePercent', color: string): MetricSeriesSpec => ({
        key,
        label: t(
            key === 'activePercent' ? 'overview.activeRespiration' : 'overview.passiveRespiration'
        ),
        color,
        tension: BEHAVIOR_LINE_TENSION,
        lineWidth: RESPIRATION_LINE_WIDTH,
        tooltipKey: `behaviorModule.tooltips.${key === 'activePercent' ? 'activeRespiration' : 'passiveRespiration'}`,
        uncertain: { points: points.map((point) => !point.certainActivePercent) },
        data: behaviorPoints(points, (point) => point[key]),
    });

    const oxygen =
        oxygenValues.length > 0
            ? behaviorBand({
                  key: 'oxygen',
                  label: oxygenLabel,
                  color: BEHAVIOR_RIVERSIDE,
                  data: behaviorPoints(points, (point) => point.oxygen),
              })
            : undefined;

    return {
        kind: 'line',
        ...behaviorXAxis(points, locale),
        series: [
            ...(showBreathingRate
                ? [respirationRateSeries(points, t('common.respirationRate'))]
                : [
                      shares('activePercent', BEHAVIOR_ORANGE),
                      shares('passivePercent', BEHAVIOR_GREEN),
                  ]),
            ...(oxygen ? [oxygen] : []),
        ],
        left: showBreathingRate
            ? {
                  label: t('common.respirationRate'),
                  unit: 'Hz',
                  ...calculateChartRange(points.map((point) => point.respiration)),
              }
            : { label: t('overview.shareOfFish'), unit: '%', min: 0, max: 100 },
        ...(oxygen
            ? {
                  right: {
                      label: oxygenLabel,
                      unit: '%',
                      ...calculateChartRange(oxygenValues, 20, false),
                      alignTicksToLeft: true,
                  },
              }
            : {}),
        locale,
        tooltipFooterLines: respirationFooter(
            input,
            showBreathingRate ? 'certainRespiration' : 'certainActivePercent'
        ),
    };
}
